// sectionTracking.ts
// Generic page-section visibility tracking — GA4 only, no product/master
// coupling. For product-context tracking on /m/ pages, use
// masterTracking.ts's useSectionVisibility instead; this is for everything
// else (homepage sections, landing pages, etc).

import { useEffect, useRef } from 'react';
import { pushToDataLayer } from './analytics.service';

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

/**
 * React hook that fires a `section_view` GA4 event once when the attached
 * element scrolls 30% into view. Disconnects after the first trigger so the
 * event fires exactly once per section per page load — this measures reach
 * ("did a session ever see this"), not repeated exposure.
 *
 * Usage:
 *   const { ref } = useSectionViewTracking('homepage_featured_grid');
 *   <div ref={ref} ...>
 */
export function useSectionViewTracking<T extends HTMLElement = HTMLElement>(
  sectionId: string
) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (!ref.current) return undefined;
    if (typeof window === 'undefined' || !window.IntersectionObserver) return undefined;

    const element = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (isProd) {
            try {
              pushToDataLayer('section_view', { section: sectionId });
            } catch {
              // Tracking must never throw and break the page
            }
          }
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sectionId]);

  return { ref };
}
